import { Button } from "@/components/ui/button";
import React from "react";
import { useGlobalStore } from "../context/globalStoreProvider";
import { cn, convertUTCToLocalDateIgnoringTimezone } from "@/lib/utils";
import { handleError } from "@/lib/apiUtils";
import { useRouter } from "next-nprogress-bar";
import { Loader2 } from "lucide-react";
import useStatusChallenge from "@/hooks/client/useStatusChallenge";
import { linearGradientContainer } from "./GondolaChallengePage";
import { add } from "date-fns";

type Props = {
  hideHeader?: boolean;
};

const CommonContainer: React.FC<Props> = ({ hideHeader }) => {
  const { user } = useGlobalStore();
  const [settingGold, setSettingGold] = React.useState(false);
  const { bookingsCount, statusChallengeInProgress, statusChallengeComplete } =
    useStatusChallenge();

  const router = useRouter();

  const setGoldMember = async () => {
    try {
      setSettingGold(true);
      await fetch("/api/user/setGondolaGold", { method: "PUT" });
      await user?.fetchUser?.();
    } catch (error) {
      handleError(error, { reportError: true });
    } finally {
      setSettingGold(false);
    }
  };

  const onExploreHotels = () => {
    router.push("/hotel/search");
  };

  return (
    <div
      className={cn(
        "flex max-w-[400px] flex-col justify-center gap-10 px-2 text-primary md:min-h-[70vh] md:max-w-[575px] lg:bg-[#0f1925] lg:px-4",
        statusChallengeInProgress || statusChallengeComplete
          ? "mt-14 md:mt-0"
          : ""
      )}
    >
      {(statusChallengeInProgress || statusChallengeComplete) &&
      user?.data.gondolaGoldStartDate ? (
        <>
          <div className="flex flex-col items-center gap-4">
            <span className="text-center text-5xl font-medium">
              You&apos;re all set!
            </span>
            <span className={cn("text-center text-xl text-[#FFFFFF80]")}>
              You&apos;ve got 5% cash back status for the next 30 days. Make
              three bookings by{" "}
              {convertUTCToLocalDateIgnoringTimezone(
                add(user?.data.gondolaGoldStartDate, { days: 30 })
              ).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}{" "}
              to keep your status until{" "}
              {convertUTCToLocalDateIgnoringTimezone(
                add(user?.data.gondolaGoldStartDate, { days: 365 })
              ).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="w-full items-center justify-center py-5">
            <div className="whitespace-nowrap text-left text-xl font-medium text-white">
              Challenge Progress:
            </div>
            <div className="flex cursor-pointer items-center gap-4">
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-600">
                <div
                  className={cn(
                    "absolute inset-0 rounded-full transition-all duration-500"
                  )}
                  style={{
                    background: linearGradientContainer.background,
                    width: `${Math.min(((bookingsCount ?? 0) / 3) * 100, 100)}%`,
                  }}
                />
              </div>
              <div className="whitespace-nowrap text-xl font-medium text-white">
                {bookingsCount}/3 stays
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Button
              className="h-14 w-full"
              style={linearGradientContainer}
              onClick={onExploreHotels}
            >
              Explore Hotels
            </Button>
          </div>
        </>
      ) : (
        <>
          {!hideHeader && (
            <div className="flex flex-col items-center gap-4">
              <span className="text-center text-2xl font-medium text-primary md:w-full md:text-left md:text-5xl">
                Earn an extra{" "}
                <strong className="text-primary-gold">5% cash back</strong> for
                the next year
              </span>
            </div>
          )}
          <div className="flex flex-col gap-4">
            <Item
              title="Triple dip on points"
              description="Gondola books directly with hotels, allowing you to earn credit card points, hotel points + status nights, AND 5% Gondola cash"
              image="/assets/gondola-challenge/triple-dip.png"
            />
            <Item
              title="3 bookings within 30 days"
              description="Lock in 5% cash back for an entire year when you make three Gondola hotel bookings within 30 days"
              image="/assets/gondola-challenge/first-booking.png"
            />
            <Item
              title="Cash redeemable towards any hotel"
              description="Gondola cash is redeemable for the cash cost of any hotel in the world"
              image="/assets/gondola-challenge/dollar.png"
            />
          </div>
          <div className="flex flex-col gap-4">
            <Button
              className="h-14 w-full"
              style={linearGradientContainer}
              onClick={setGoldMember}
              disabled={settingGold}
            >
              {settingGold ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Start the Challenge"
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

type ItemProps = {
  title: string;
  description: string;
  image: string;
};

const Item: React.FC<ItemProps> = ({ title, description, image }) => {
  // const textColor = colorMode === "light" ? "#091627" : "#FFFFFF";
  // const textColorDescription =
  //   colorMode === "light" ? "#09162780" : "#FFFFFF80";
  return (
    <div className="flex items-center gap-4">
      <img src={image} alt={image} />
      <div className="flex flex-col">
        <span className="font-bold md:text-lg" style={{ color: "#ffffff" }}>
          {title}
        </span>
        <span className="text-xs md:text-base" style={{ color: "#ffffff80" }}>
          {description}
        </span>
      </div>
    </div>
  );
};

export default CommonContainer;
